/**
 * WCAG contrast helpers.
 *
 * Tenant colors are authored in the Vendure admin and applied at runtime, so a
 * brand color that fails AA against the tenant's own background can't be caught
 * at build time. These helpers derive accessible *text* colors from the raw
 * brand colors — see the `--tenant-primary-text` / `--tenant-on-primary` /
 * `--tenant-muted` vars set in `src/hooks/channelLoader.ts`.
 */

export const WCAG_AA_NORMAL = 4.5
export const WCAG_AA_LARGE = 3

export type Rgb = { r: number; g: number; b: number }

/** Accepts `#rgb`, `#rrggbb` (with or without `#`). Returns null when unparseable. */
export function hexToRgb(hex: string): Rgb | null {
  const normalized = hex.trim().replace(/^#/, '')

  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  }
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(rgb: Rgb): number {
  const linearize = (channel: number) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * linearize(rgb.r) + 0.7152 * linearize(rgb.g) + 0.0722 * linearize(rgb.b)
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). Returns 1 on unparseable input. */
export function contrastRatio(foreground: string, background: string): number {
  const fg = hexToRgb(foreground)
  const bg = hexToRgb(background)
  if (!fg || !bg) return 1

  const lighter = Math.max(relativeLuminance(fg), relativeLuminance(bg))
  const darker = Math.min(relativeLuminance(fg), relativeLuminance(bg))
  return (lighter + 0.05) / (darker + 0.05)
}

/** Linear blend of two hex colors. `weight` is how much of `from` survives (0..1). */
export function mixHex(from: string, to: string, weight: number): string {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  if (!a || !b) return from

  const clamped = Math.max(0, Math.min(1, weight))
  return rgbToHex({
    r: a.r * clamped + b.r * (1 - clamped),
    g: a.g * clamped + b.g * (1 - clamped),
    b: a.b * clamped + b.b * (1 - clamped),
  })
}

/**
 * Returns `foreground` unchanged when it already meets `target` against
 * `background`; otherwise blends it toward black (on a light background) or
 * white (on a dark one) in small steps until it passes.
 *
 * Blending toward black/white rather than rotating hue keeps the brand color
 * recognisable — #0093D0 on white becomes a darker blue, not a different color.
 */
export function ensureContrast(
  foreground: string,
  background: string,
  target: number = WCAG_AA_NORMAL,
): string {
  const fg = hexToRgb(foreground)
  const bg = hexToRgb(background)
  if (!fg || !bg) return foreground

  if (contrastRatio(foreground, background) >= target) return foreground

  // Push away from the background: darken on light backgrounds, lighten on dark.
  const anchor = relativeLuminance(bg) > 0.5 ? '#000000' : '#ffffff'

  const STEPS = 40
  for (let step = 1; step <= STEPS; step++) {
    const candidate = mixHex(foreground, anchor, 1 - step / STEPS)
    if (contrastRatio(candidate, background) >= target) return candidate
  }

  return anchor
}

/** Black or white — whichever contrasts better with `background`. For text sitting on a filled brand surface. */
export function pickOnColor(background: string): string {
  return contrastRatio('#ffffff', background) >= contrastRatio('#000000', background)
    ? '#ffffff'
    : '#000000'
}
